import React from 'react';
import { 
    Box, 
    Typography, 
    Paper,
    Grid,
    Divider,
    Chip
} from '@mui/material';
import { 
    Receipt as ReceiptIcon,
    LocalShipping as LocalShippingIcon,
    Compare as CompareIcon
} from '@mui/icons-material';
import { CitySums, CityTerminal } from './types';
import { formatSum } from './utils';

interface SummaryDisplayProps {
    sums: CitySums;
    citySums: CitySums;
    cityTerminals: CityTerminal[];
}

const SummaryDisplay: React.FC<SummaryDisplayProps> = ({
    sums,
    citySums,
    cityTerminals
}) => {
    const totalDiff = Math.abs(sums.total - citySums.total);
    const hasSignificantDiff = totalDiff > 0.1;

    return (
        <Grid container spacing={2}>
            {/* Выписка */}
            <Grid item xs={12} sm={6} lg={4}>
                <Paper 
                    elevation={1} 
                    sx={{ 
                        p: 2, 
                        border: '1px solid',
                        borderColor: 'primary.light'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <ReceiptIcon color="primary" fontSize="small" />
                        <Typography variant="h6" color="primary">
                            Выписка
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Общая сумма операций:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {formatSum(sums.total)} ₸
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {cityTerminals.map(terminal => {
                            const pharmacySum = sums[terminal.created_by] || 0;
                            return (
                                <Box key={terminal.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5, borderBottom: '1px solid #eee' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {terminal.created_by}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                            {formatSum(pharmacySum)} ₸
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {terminal.address}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>
            </Grid>

            {/* Доставка аптеки */}
            <Grid item xs={12} sm={6} lg={4}>
                <Paper 
                    elevation={1} 
                    sx={{ 
                        p: 2, 
                        border: '1px solid',
                        borderColor: 'info.light'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <LocalShippingIcon color="info" fontSize="small" />
                        <Typography variant="h6" color="info.main">
                            Доставка аптеки
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Общая сумма операций:
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {formatSum(citySums.total)} ₸
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {cityTerminals.map(terminal => {
                            const pharmacySum = citySums[terminal.created_by] || 0;
                            return (
                                <Box key={terminal.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5, borderBottom: '1px solid #eee' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {terminal.created_by}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                            {formatSum(pharmacySum)} ₸
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {terminal.address}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>
            </Grid>

            {/* Разница */}
            <Grid item xs={12} sm={12} lg={4}>
                <Paper 
                    elevation={1} 
                    sx={{ 
                        p: 2, 
                        border: '1px solid',
                        borderColor: hasSignificantDiff ? 'warning.light' : 'success.light'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CompareIcon color={hasSignificantDiff ? 'warning' : 'success'} fontSize="small" />
                        <Typography variant="h6" color={hasSignificantDiff ? 'warning.main' : 'success.main'}>
                            Разница
                        </Typography>
                        {hasSignificantDiff && (
                            <Chip 
                                label="Есть расхождения" 
                                color="warning" 
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Общая разница:
                        </Typography>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                fontWeight: 'bold',
                                color: hasSignificantDiff ? 'warning.main' : 'success.main'
                            }}
                        >
                            {formatSum(totalDiff)} ₸
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 1 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {cityTerminals.map(terminal => {
                            const pharmacyKey = terminal.created_by;
                            const diff = Math.abs((sums[pharmacyKey] || 0) - (citySums[pharmacyKey] || 0));
                            const hasPharmacyDiff = diff > 0.1;
                            
                            return (
                                <Box key={terminal.id} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5, borderBottom: '1px solid #eee' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {terminal.created_by}
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                fontWeight: 'medium',
                                                color: hasPharmacyDiff ? 'warning.main' : 'success.main'
                                            }}
                                        >
                                            {formatSum(diff)} ₸
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {terminal.address}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default SummaryDisplay;
